/**
 * EO «Конфигуратор → Цены» — панель суб-таба по эталону
 * design-references/screen-18-eo-config-prices-mk.html + screen-18-prices.js.
 *
 * Сводный прайс сети (A40): BranchTabs с «ВСЕ»-дефолтом (фильтр ТОЛЬКО
 * таблицы квестов — услуги общесетевые, A41), группы-строки филиалов,
 * персональный порядок drag'ом (useStaffPref: price_rows_order по филиалам,
 * svc_rows_order плоско). Авто-сейв A38 — commit ячейки NumPadPop = запись
 * (usePricesAdmin), кнопки «Сохранить» нет, dirty-guard не нужен. A39 — нули
 * живые. Справки-«?» — TipBox с текстами эталона ДОСЛОВНО (js:384-398).
 * PullHud + usePullToRefresh (A43.3); снекбар-хост A43.2 — общий для панели
 * (undo-мелочи; первый потребитель — справочник услуг PRC-4).
 */
import { useState, type MouseEvent } from "react";
import { BranchTabs } from "../../components/grid";
import { NumPadPop, PullHud, useUndoSnackbar } from "../../components/shared";
import { useAppData } from "../../hooks/useAppData";
import { usePricesAdmin } from "../../hooks/usePricesAdmin";
import { useAppReload, usePullToRefresh } from "../../hooks/usePullToRefresh";
import { useStaffPref } from "../../hooks/useStaffPrefs";
import { PopMenu, TipBox, type PopCfg } from "./PriceRow";
import { QuestPriceTable, ServicePriceTable } from "./PriceTable";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import css from "./prices.module.css";

const toggled = (s: ReadonlySet<string>, id: string): Set<string> => {
  const n = new Set(s);
  if (n.has(id)) n.delete(id); else n.add(id);
  return n;
};

export function EoConfigPrices() {
  const app = useAppData();
  const pa = usePricesAdmin();
  const [brFilter, setBrFilter] = useState("all");
  const [qOpen, setQOpen] = useState<ReadonlySet<string>>(new Set());
  const [sOpen, setSOpen] = useState<ReadonlySet<string>>(new Set());
  const [ord, setOrd] = useStaffPref<Record<string, string[]>>("price_rows_order", {});
  const [sord, setSord] = useStaffPref<string[]>("svc_rows_order", []);
  const [pop, setPop] = useState<PopCfg | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; onPick: () => void } | null>(null);
  const [tip, setTip] = useState<{ key: "q" | "v"; anchor: HTMLElement } | null>(null);
  const snack = useUndoSnackbar();
  const reloadApp = useAppReload();
  const ptr = usePullToRefresh(() => { pa.refetch(); return reloadApp(); });

  const active = app.branches.filter((b) => b.active);
  /* Фильтр квест-таблицы; филиалы без квестов не рендерятся (js:112). */
  const shown = (brFilter === "all" ? active : active.filter((b) => b.id === brFilter))
    .filter((b) => app.quests.some((q) => q.branch_id === b.id));
  const svcs = orderBy(app.masterClasses.filter((m) => m.active), sord, (m) => m.id);

  /* Личный порядок (A40, ordered js:67-69): pref поверх sort_order. */
  const questsOf = (bid: string) => {
    const qs = app.quests.filter((q) => q.branch_id === bid)
      .sort((a, z) => (a.sort_order ?? 0) - (z.sort_order ?? 0));
    return orderBy(qs, ord[bid] ?? [], (q) => q.id);
  };

  const tipToggle = (key: "q" | "v") => (e: MouseEvent<HTMLButtonElement>) => {
    const anchor = e.currentTarget;
    setTip((t) => (t?.key === key ? null : { key, anchor }));
  };

  /* Провал загрузки НЕ рендерит таблицы нулей (нули A39 «живые» и выглядят
     легитимно) — скелетон + errHint; PTR работает и здесь (ptr.ref callback). */
  if ((app.loading && app.quests.length === 0) || !pa.loaded) {
    return (
      <div className={cfg.scroll} ref={ptr.ref}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        {pa.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "8px 10px 0" }}>
            Цены не загрузились — потяните вниз, чтобы повторить.
          </div>
        ) : null}
        <div className={cfg.skel}>
          <div className={`${cfg.sk} ${cfg.skTitle}`} />
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
        </div>
      </div>
    );
  }

  return (
    <>
      <BranchTabs branches={active} active={brFilter} onChange={setBrFilter} />
      <div className={cfg.scroll} ref={ptr.ref}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        <div className={qcss.s17head} style={{ marginTop: 5 }}>
          <h2 className={cfg.secT}>Цены на квесты</h2>
          <button type="button" className={css.qmark} data-qmark="" aria-label="Справка" onClick={tipToggle("q")}>
            <span>?</span>
          </button>
        </div>
        {pa.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "0 8px" }}>
            Часть цен не загрузилась — их правки заблокированы; потяните вниз, чтобы обновить.
          </div>
        ) : null}
        {pa.hint ? <div className={cfg.errHint} style={{ margin: "0 8px" }}>{pa.hint}</div> : null}
        {shown.length ? (
          <div className={css.pwrap}>
            <QuestPriceTable
              branches={shown} grouped={brFilter === "all"} questsOf={questsOf} pa={pa}
              qOpen={qOpen} onToggle={(id) => setQOpen((s) => toggled(s, id))}
              onReorder={(bid, ids) => setOrd({ ...ord, [bid]: ids })}
              openPop={setPop}
            />
          </div>
        ) : (
          <div className={cfg.teaEmpty}>Нет квестов у филиала — добавьте в «Квесты и слоты».</div>
        )}
        <div className={qcss.s17head}>
          <h2 className={`${cfg.secT} ${css.cy}`}>Цены на доп. услуги</h2>
          <button type="button" className={css.qmark} data-qmark="" aria-label="Справка" onClick={tipToggle("v")}>
            <span>?</span>
          </button>
        </div>
        {svcs.length ? (
          <div className={css.pwrap}>
            <ServicePriceTable
              services={svcs} branches={active} pa={pa}
              sOpen={sOpen} onToggle={(id) => setSOpen((s) => toggled(s, id))}
              onReorder={setSord} openPop={setPop}
              openMenu={(anchor, onPick) => setMenu({ anchor, onPick })}
            />
          </div>
        ) : (
          <div className={cfg.teaEmpty}>Нет доп. услуг — прайс пуст.</div>
        )}
      </div>
      {tip ? (
        <TipBox anchor={tip.anchor} onClose={() => setTip(null)}>
          {tip.key === "q" ? <QTip /> : <VTip />}
        </TipBox>
      ) : null}
      {menu ? (
        <PopMenu anchor={menu.anchor} label="Сбросить к сетевой" onPick={menu.onPick} onClose={() => setMenu(null)} />
      ) : null}
      <NumPadPop
        open={!!pop} anchor={pop?.anchor ?? null} label={pop?.label} mode={pop?.mode}
        unit={pop?.unit} max={pop?.max} value={pop?.value}
        onCommit={(v) => pop?.onCommit(v)} onClose={() => setPop(null)}
      />
      {snack.element}
    </>
  );
}

/** Персональный порядок: pos по pref, хвост — исходный порядок (js:67-69). */
function orderBy<T>(items: T[], pref: string[], idOf: (x: T) => string): T[] {
  if (!pref.length) return items;
  const pos = (id: string) => { const i = pref.indexOf(id); return i < 0 ? 1e3 : i; };
  return [...items].sort((a, c) => pos(idOf(a)) - pos(idOf(c)) || items.indexOf(a) - items.indexOf(c));
}

/* Справки — тексты эталона ДОСЛОВНО (js:384-398). */
function QTip() {
  return (
    <>
      <p><b>Базовая цена</b> до N игроков, далее +₽ за каждого. Мин. ₽ — минимальная сумма с команды.</p>
      <p><b>0</b> — не применяется. Любой ноль можно править.</p>
      <p><b>Двойной тап</b> по названию — раскрыть спецверсии квеста. <b>Точка</b> у названия — у квеста есть спецверсии.</p>
      <p><b>Зажмите</b> название — измените порядок. Порядок сохраняется лично для вас.</p>
      <p><b>Филиалы</b> сверху фильтруют таблицу квестов.</p>
      <p><b>Свайп вправо</b> по строке — дубликат: откроется форма копии.</p>
      <p><b>Потяните вниз</b> от верха списка — обновить данные.</p>
    </>
  );
}
function VTip() {
  return (
    <>
      <p>Услуги общесетевые — фильтр филиалов на них не действует.</p>
      <p><b>Двойной тап</b> по названию — цены филиалов: <b>приглушённое</b> наследуется от сетевой строки, <b>яркое</b> — своя цена филиала (точка у названия). Зажмите яркую ячейку — сброс к сетевой.</p>
      <p><b>0</b> — не применяется. Зажмите название — персональный порядок.</p>
      <p><b>Свайп вправо</b> по строке в справочнике — дубликат: откроется форма копии.</p>
      <p><b>✕</b> удаляет сразу — внизу 5 секунд можно ОТМЕНИТЬ.</p>
      <p><b>Потяните вниз</b> от верха списка — обновить данные.</p>
    </>
  );
}
